import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardMedia,
    Button,
    Chip,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { useParams, Link, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import SportsMartialArtsIcon from '@mui/icons-material/SportsMartialArts';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';

const EventDetail = () => {
    const { id } = useParams();
    const location = useLocation();
    // Les deux premiers événements sont favoris par défaut
    const [isFavorite, setIsFavorite] = useState(id === '1' || id === '2');

    // Faire défiler vers le haut de la page quand la route change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // Données simulées - dans une vraie app, cela viendrait d'une API
    const eventData = {
        1: {
            id: 1,
            title: 'Football Match - Liga 1',
            description: 'Alianza Lima vs Universitario de Deportes - The Peruvian football classic. A historic match between the two most titled clubs in the country.',
            longDescription: 'This match is much more than just a sporting clash. It is a historic rivalry that has divided Lima for decades. Alianza Lima, founded in 1901, and Universitario de Deportes, founded in 1924, compete for the supremacy of Peruvian football. The national stadium will be filled with passion and emotion.',
            date: 'December 15, 2024',
            time: '20:00',
            location: 'Estadio Nacional, Lima',
            city: 'Lima',
            address: 'Av. José Díaz s/n, Cercado de Lima 15046, Peru',
            image: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&h=400&fit=crop',
            sport: 'Football',
            price: '50-150 PEN',
            category: 'Match',
            capacity: '45,000',
            organizer: 'Peruvian Football Federation',
            icon: <SportsSoccerIcon />,
            tickets: [
                { category: 'General', price: 50, available: true },
                { category: 'Preferential', price: 80, available: true },
                { category: 'VIP', price: 150, available: true },
            ],
            highlights: [
                'Historic match between the two most titled clubs',
                'Electric atmosphere at the national stadium',
                'Chance to meet football legends',
                'Musical and cultural entertainment before the match',
            ],
            rules: [
                'No dangerous objects allowed',
                'Arrival recommended 1h before the start',
                'Team colors clothing allowed',
                'Strict compliance with safety instructions',
            ],
        },
        2: {
            id: 2,
            title: 'Basketball Championship',
            description: 'National basketball championship final - The best of Peruvian basketball compete for the national crown.',
            longDescription: 'The national basketball championship final brings together the best teams in the country in an intense competition. The players give their best to win the coveted title.',
            date: 'December 20, 2024',
            time: '19:30',
            location: 'Coliseo Eduardo Dibós, Lima',
            city: 'Lima',
            address: 'Av. Arequipa 2100, Lince 15073, Peru',
            image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&h=400&fit=crop',
            sport: 'Basketball',
            price: '30-80 PEN',
            category: 'Final',
            capacity: '8,000',
            organizer: 'Peruvian Basketball Federation',
            icon: <SportsBasketballIcon />,
            tickets: [
                { category: 'General', price: 30, available: true },
                { category: 'Preferential', price: 50, available: true },
                { category: 'VIP', price: 80, available: true },
            ],
            highlights: [
                'National championship final',
                'International level of play',
                'Halftime shows and entertainment',
                'Meet the players after the match',
            ],
            rules: [
                'No smoking inside the venue',
                'Professional cameras prohibited',
                'Respect other spectators',
                'Follow staff instructions',
            ],
        },
    };

    const event = eventData[id];

    if (!event) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography variant="h4" textAlign="center">
                    Event not found
                </Typography>
                <Box sx={{ textAlign: 'left', mt: 2 }}>
                    <Button component={Link} to="/events" variant="contained">
                        Back to events
                    </Button>
                </Box>
            </Container>
        );
    }

    const getSportColor = (sport) => {
        const colors = {
            'Football': '#D91023',
            'Basketball': '#FF6B35',
            'Volleyball': '#4ECDC4',
            'Tennis': '#45B7D1',
            'Arts Martiaux': '#96CEB4',
        };
        return colors[sport] || '#666';
    };

    return (
        <Box sx={{ py: 4 }}>
            <Container maxWidth="lg">
                {/* Back Button */}
                <Box sx={{ textAlign: 'left', mb: 3 }}>
                    <Button
                        component={Link}
                        to="/events"
                        startIcon={<ArrowBackIcon />}
                    >
                        Back to events
                    </Button>
                </Box>

                <Grid container spacing={4}>
                    {/* Main Content */}
                    <Grid item xs={12}>
                        {/* Event Image */}
                        <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
                            <CardMedia
                                component="img"
                                height="400"
                                image={event.image}
                                alt={event.title}
                            />
                        </Card>

                        {/* Event Info */}
                        <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{ color: getSportColor(event.sport), mr: 2, display: 'flex', alignItems: 'center' }}>
                                    {React.cloneElement(event.icon, { sx: { fontSize: '2.5rem' } })}
                                </Box>
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="h4" component="h1" sx={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                                        {event.title}
                                    </Typography>
                                    <Chip
                                        label={event.category}
                                        sx={{ backgroundColor: 'primary.main', color: 'white' }}
                                    />
                                    <Box
                                        sx={{
                                            cursor: 'pointer',
                                            color: '#FF6B6B',
                                            background: 'white',
                                            borderRadius: '50%',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.10)',
                                            p: 1,
                                            width: 40,
                                            height: 40,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'box-shadow 0.2s, color 0.2s',
                                            '&:hover': {
                                                color: '#d94a4a',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.16)',
                                            },
                                        }}
                                        onClick={() => setIsFavorite(!isFavorite)}
                                        title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                    >
                                        {isFavorite ? (
                                            <StarIcon fontSize="medium" />
                                        ) : (
                                            <StarBorderIcon fontSize="medium" />
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, textAlign: 'left' }}>
                                {event.longDescription}
                            </Typography>

                            <Divider sx={{ my: 3 }} />

                            {/* Event Details */}
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <List>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CalendarTodayIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Date"
                                                secondary={event.date}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <AccessTimeIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Time"
                                                secondary={event.time}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <LocationOnIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Location"
                                                secondary={<a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#FF6B6B', textDecoration: 'underline', fontWeight: 700 }}>{event.location}</a>}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <List>
                                        <ListItem>
                                            <ListItemIcon>
                                                <AttachMoneyIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Price"
                                                secondary={event.price}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <PeopleIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Capacity"
                                                secondary={event.capacity + ' seats'}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <InfoIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Organizer"
                                                secondary={event.organizer}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Highlights */}
                        <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
                            <Typography variant="h5" gutterBottom>
                                Event Highlights
                            </Typography>
                            <List>
                                {event.highlights.map((highlight, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary={highlight} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>

                        {/* Rules */}
                        <Paper sx={{ p: 4, borderRadius: 3 }}>
                            <Typography variant="h5" gutterBottom>
                                Rules & Guidelines
                            </Typography>
                            <List>
                                {event.rules.map((rule, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            <InfoIcon color="warning" />
                                        </ListItemIcon>
                                        <ListItemText primary={rule} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default EventDetail; 